import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Package2,
  Users,
  Wrench,
  Activity,
  BarChart,
  Settings2,
  ListChecks,
  MessageCircle,
  Calendar,
  FileText,
  UserCircle,
  FileBarChart2,
  Megaphone,
  Calculator,
  Shield,
  ChevronDown,
  Store,
  ShoppingCart,
} from "lucide-react";
import { useBusiness } from "../contexts/BusinessContext";
import { supabase } from "../lib/supabase";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { useApplicationSettingsContext } from "../contexts/ApplicationSettingsContext";
import { PageId } from "../hooks/useApplicationSettings";
import { usePermissions } from "../contexts/PermissionContext";
import { useSuperAdmin } from "../hooks/useSuperAdmin";
import { useUnreadOpportunitiesContext } from "../contexts/UnreadOpportunitiesContext";
// Removed usePendingTimeOffCount hook - now handling real-time directly

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  text: string;
  badgeCount?: number;
  onClick?: () => void;
  showIndicator?: boolean;
  beta?: boolean;
}

function NavLink({ to, icon, text, badgeCount, onClick, showIndicator, beta }: NavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`group flex items-center px-3 py-2 text-sm transition-all duration-200 rounded-lg ${
        isActive
          ? "bg-gray-900 text-white shadow-sm border border-gray-800"
          : "text-gray-300 hover:bg-gray-700 hover:text-white"
      }`}
    >
      <div className={`flex items-center justify-center w-5 h-5 mr-3 relative ${
        isActive ? "text-white" : "text-gray-400 group-hover:text-gray-300"
      }`}>
        {icon}
        {showIndicator && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
        )}
      </div>
      
      <div className="flex items-center space-x-2 flex-1">
        <span className={`font-medium ${isActive ? "text-white" : "text-white group-hover:text-white"}`}>
          {text}
        </span>
        {beta && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-600 text-white">
            BETA
          </span>
        )}
      </div>
      
      {/* Badge for unread count - only show when count is greater than 0 */}
      {typeof badgeCount === 'number' && badgeCount > 0 && (
        <div className="ml-auto">
          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-medium text-white bg-red-500 rounded-full">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        </div>
      )}
    </Link>
  );
}

// Reports Dropdown Component
function ReportsDropdown({ canAccess, isPageDisabled }: { canAccess: any, isPageDisabled: (pageId: PageId) => boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  // Check if any reports page is active
  const isReportsActive = location.pathname.includes('/sales-reports') || 
                         location.pathname.includes('/team-reports') || 
                         location.pathname.includes('/supplies-usage');

  // Don't show dropdown if no reports are accessible
  const hasAnyReports = (!isPageDisabled('sales-reports') && canAccess.salesReports) ||
                       (!isPageDisabled('team-reports') && canAccess.teamReports) ||
                       (!isPageDisabled('supplies-usage') && canAccess.suppliesUsage);

  if (!hasAnyReports) return null;

  return (
    <div>
      {/* Reports Dropdown Toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group flex items-center w-full px-3 py-2 text-sm transition-all duration-200 rounded-lg ${
          isReportsActive
            ? "bg-gray-900 text-white shadow-sm border border-gray-800"
            : "text-gray-300 hover:bg-gray-700 hover:text-white"
        }`}
      >
        <div className={`flex items-center justify-center w-5 h-5 mr-3 relative ${
          isReportsActive ? "text-white" : "text-gray-400 group-hover:text-gray-300"
        }`}>
          <BarChart className="h-4 w-4" />
        </div>
        
        <div className="flex items-center space-x-2 flex-1">
          <span className={`font-medium ${isReportsActive ? "text-white" : "text-white group-hover:text-white"}`}>
            Reports
          </span>
        </div>
        
        <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>
      </button>

      {/* Reports Dropdown Items */}
      {isOpen && (
        <div className="ml-8 mt-1 space-y-0">
          {!isPageDisabled('sales-reports') && canAccess.salesReports && (
            <NavLink
              to="/sales-reports"
              icon={<BarChart className="h-4 w-4" />}
              text="Sales Reports"
            />
          )}
          
          {!isPageDisabled('team-reports') && canAccess.teamReports && (
            <NavLink
              to="/team-reports"
              icon={<Users className="h-4 w-4" />}
              text="Team Reports"
            />
          )}
          
          {!isPageDisabled('supplies-usage') && canAccess.suppliesUsage && (
            <NavLink
              to="/supplies-usage"
              icon={<Package2 className="h-4 w-4" />}
              text="Supplies Usage"
            />
          )}
        </div>
      )}
    </div>
  );
}

// Products Dropdown Component
function ProductsDropdown({ canAccess, isPageDisabled }: { canAccess: any, isPageDisabled: (pageId: PageId) => boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  // Check if any products page is active
  const isProductsActive = location.pathname.includes('/stock') || 
                          location.pathname.includes('/sell-products') ||
                          location.pathname.includes('/online-store');

  // Don't show dropdown if supplies access is disabled
  if (isPageDisabled('supplies') || !canAccess.supplies) return null;

  return (
    <div>
      {/* Products Dropdown Toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group flex items-center w-full px-3 py-2 text-sm transition-all duration-200 rounded-lg ${
          isProductsActive
            ? "bg-gray-900 text-white shadow-sm border border-gray-800"
            : "text-gray-300 hover:bg-gray-700 hover:text-white"
        }`}
      >
        <div className={`flex items-center justify-center w-5 h-5 mr-3 relative ${
          isProductsActive ? "text-white" : "text-gray-400 group-hover:text-gray-300"
        }`}>
          <Package2 className="h-4 w-4" />
        </div>
        
        <div className="flex items-center space-x-2 flex-1">
          <span className={`font-medium ${isProductsActive ? "text-white" : "text-white group-hover:text-white"}`}>
            Products
          </span>
        </div>
        
        <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>
      </button>

      {/* Products Dropdown Items */}
      {isOpen && (
        <div className="ml-8 mt-1 space-y-0">
          <NavLink
            to="/stock"
            icon={<Package2 className="h-4 w-4" />}
            text="Inventory"
          />
          
          <NavLink
            to="/sell-products"
            icon={<ShoppingCart className="h-4 w-4" />}
            text="Sell Products"
          />
          
          <NavLink
            to="/online-store"
            icon={<Store className="h-4 w-4" />}
            text="Online Store"
          />
        </div>
      )}
    </div>
  );
}

interface LeftNavigationProps {
  hasSettings: boolean;
  isDevelopment?: boolean;
  timeclockPermissionLevel?: "manage" | "view" | "disabled";
  hasCalendar?: boolean;
  hasBookings?: boolean;
  proposalsAccess?: "manage" | "view" | "disabled";
  hasPayments?: boolean;
  hasCustomers?: boolean;
  hasMessages?: boolean;
  hasTasks?: boolean;
  isMobile?: boolean;
  bottomNavItems?: string[];
  sidebarOpen?: boolean;
}



export default function LeftNavigation({
  hasSettings,
  hasCalendar = false,
  hasBookings = false,
  proposalsAccess = "disabled",
  hasCustomers = false,
  hasTasks = false,
  isMobile = false,
  sidebarOpen = true,
}: LeftNavigationProps) {
  const { selectedBusiness } = useBusiness();
  const { isSuperAdmin } = useSuperAdmin();
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();

  // PWA Detection
  const [isPWA, setIsPWA] = useState(false);

  // Unread messages count state
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Unread opportunities count
  const { unreadCount: unreadOpportunitiesCount } = useUnreadOpportunitiesContext();

  // Pending time off requests count - managed directly with real-time
  const [pendingTimeOffCount, setPendingTimeOffCount] = useState(0);
  
  // Pending payroll requests count - managed directly with real-time
  const [pendingPayrollRequestsCount, setPendingPayrollRequestsCount] = useState(0);
  
  


  useEffect(() => {
    // Detect PWA mode
    const detectPWA = () => {
      const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone === true ||
                       document.body.classList.contains('pwa-mode');
      setIsPWA(isPWAMode);
    };

    detectPWA();
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', detectPWA);
    
    return () => mediaQuery.removeEventListener('change', detectPWA);
  }, []);

  // Fetch initial unread message count and set up realtime subscription
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    // Function to fetch current unread count
    const fetchUnreadCount = async () => {
      try {
        if (!supabase) {
          console.error('[NAV BADGE] Supabase client not initialized');
          return;
        }

        const { count, error } = await supabase
          .from('all_messages')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', selectedBusiness.id)
          .eq('read', 'unread')
          .eq('direction', 'inbound'); // Only count inbound unread messages

        if (error) {
          console.error('[NAV BADGE] Error fetching unread count:', error);
          return;
        }

        const newCount = count || 0;
        console.log('[NAV BADGE] 🔢 Updating badge count:', newCount);
        setUnreadMessagesCount(newCount);
      } catch (error) {
        console.error('[NAV BADGE] Error fetching unread count:', error);
      }
    };

    // Fetch initial count
    fetchUnreadCount();

    console.log('[NAV BADGE] 🔄 Setting up navigation badge subscription for business:', selectedBusiness.id);

    // Set up realtime subscription with more specific event handling
    const subscription = supabase
      .channel(`unread_messages_nav_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[NAV BADGE] 📨 Received database event:', {
            eventType: payload.eventType,
            messageId: (payload.new as any)?.id || (payload.old as any)?.id,
            direction: (payload.new as any)?.direction || (payload.old as any)?.direction,
            read: (payload.new as any)?.read || (payload.old as any)?.read
          });
          
          // Add a small delay to ensure database consistency
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .subscribe((status) => {
        console.log('[NAV BADGE] 📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('[NAV BADGE] ✅ Navigation badge subscription active');
        }
      });

    // Set up a periodic refresh as backup (every 30 seconds)
    const periodicRefresh = setInterval(() => {
      console.log('[NAV BADGE] 🔄 Periodic refresh of badge count');
      fetchUnreadCount();
    }, 30000);

    // Listen for custom events from Messages page for immediate updates
    const handleUnreadMessagesChanged = () => {
      console.log('[NAV BADGE] 📢 Received local update event, refreshing badge');
      fetchUnreadCount();
    };

    window.addEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);

    // Cleanup subscription on unmount or business change
    return () => {
      console.log('[NAV BADGE] 🧹 Cleaning up navigation badge subscription');
      subscription.unsubscribe();
      clearInterval(periodicRefresh);
      window.removeEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);
    };
  }, [selectedBusiness?.id]);

  // Fetch pending time off requests count and set up real-time subscription
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) {
      setPendingTimeOffCount(0);
      return;
    }

    // Function to fetch current pending time off count
    const fetchPendingTimeOffCount = async () => {
      try {
        if (!supabase) return;
        
        const { count, error } = await supabase
          .from('time_off_requests')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', selectedBusiness.id)
          .eq('status', 'pending');

        if (error) {
          console.error('[TIME OFF BADGE] Error fetching pending count:', error);
          setPendingTimeOffCount(0);
          return;
        }

        setPendingTimeOffCount(count || 0);
      } catch (error) {
        console.error('[TIME OFF BADGE] Error fetching pending count:', error);
        setPendingTimeOffCount(0);
      }
    };

    // Fetch initial count
    fetchPendingTimeOffCount();

    // Set up real-time subscription for time off requests
    const timeOffSubscription = supabase!
      .channel(`pending_time_off_nav_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_off_requests',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          // Re-fetch count whenever any time off request changes for this business
          fetchPendingTimeOffCount();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount or business change
    return () => {
      supabase!.removeChannel(timeOffSubscription);
    };
  }, [selectedBusiness?.id]);

  // Fetch initial pending payroll requests count and set up realtime subscription
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) {
      setPendingPayrollRequestsCount(0);
      return;
    }

    // Function to fetch current pending payroll requests count
    const fetchPendingPayrollRequestsCount = async () => {
      try {
        if (!supabase) return;
        
        const { count, error } = await supabase
          .from('payroll_request')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', selectedBusiness.id)
          .eq('status', 'pending');

        if (error) {
          console.error('[PAYROLL BADGE] Error fetching pending count:', error);
          setPendingPayrollRequestsCount(0);
          return;
        }

        setPendingPayrollRequestsCount(count || 0);
      } catch (error) {
        console.error('[PAYROLL BADGE] Error fetching pending count:', error);
        setPendingPayrollRequestsCount(0);
      }
    };

    // Fetch initial count
    fetchPendingPayrollRequestsCount();

    // Set up real-time subscription for payroll requests
    const payrollRequestsSubscription = supabase!
      .channel(`pending_payroll_requests_nav_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_request',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          // Re-fetch count whenever any payroll request changes for this business
          fetchPendingPayrollRequestsCount();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount or business change
    return () => {
      supabase!.removeChannel(payrollRequestsSubscription);
    };
  }, [selectedBusiness?.id]);

  // Always show all navigation items in the left navigation (bottom nav has been removed)
  const shouldShowInLeftNav = (path: string) => {
    // Always show everything - bottom navigation has been removed
    return true;
  };

  // Helper to check if a page should be shown based on permissions and disabled status
  const shouldShowPage = (pageId: PageId, hasPermission: boolean) => {
    return hasPermission && !isPageDisabled(pageId);
  };

  return (
    <nav className={`flex flex-col h-full left-navigation p-2 bg-black ${
      isPWA && sidebarOpen ? 'pt-8' : isMobile ? 'pt-8' : ''
    } ${isPWA && sidebarOpen ? 'pwa-left-nav' : ''
    }`} style={{
      // Only apply PWA-specific styles when sidebar is open
      ...(isPWA && sidebarOpen ? {
        height: '100vh',
        minHeight: 'auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '220px', // Fixed sidebar width instead of 100%
        maxWidth: '220px',
        zIndex: 40,
        backgroundColor: '#000000', // Black background
        borderRight: '1px solid #1f2937' // Dark border
      } : {
        // Regular styles when not PWA or sidebar closed
        height: isPWA ? 'auto' : 'auto',
        minHeight: isPWA ? 'auto' : '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isMobile ? '80px' : '20px',
        position: isPWA ? 'relative' : 'sticky',
        top: isPWA ? 'auto' : 0,
        left: isPWA ? 'auto' : 'auto',
        width: isPWA ? 'auto' : 'auto',
        zIndex: isPWA ? 'auto' : 'auto'
      }),
      // Hide content when sidebar is closed
      display: sidebarOpen ? 'flex' : 'none'
    }}>
      {/* Only render content when sidebar is open */}
      {sidebarOpen && (
        <div className="flex flex-col h-full flex-grow w-full" style={{
          // Ensure content can scroll properly and stays within sidebar width
          minHeight: 'max-content',
          maxWidth: '100%',
          overflowX: 'hidden', // Prevent horizontal overflow
          overflowY: 'auto' // Allow vertical scrolling
        }}>
          {/* Nexus Logo */}
          <div className={`px-4 ${isMobile || isPWA ? 'pt-0 pb-0 mb-0' : 'pt-1 pb-2 mb-2'}`}>
            <img 
              src="/nexuslogowhite (512 x 512).png" 
              alt="Nexus" 
              className="h-8 w-auto"
            />
          </div>

          <div className={`flex-1 ${isMobile || isPWA ? 'space-y-2' : 'space-y-4'} pr-1 w-full`} style={{
            // Allow content to expand and scroll but constrain width
            flexShrink: 0,
            maxWidth: '100%'
          }}>

            {/* All Navigation Items - Reorganized by User Request */}
            <div className="space-y-0">
              {/* Section 1: Core Dashboard & Communication */}
              {canAccess.kpiDashboard && (
                <NavLink
                  to="/kpi-dashboard"
                  icon={<Activity className="h-4 w-4" />}
                  text="KPI Dashboard"
                />
              )}
              
              {/* Messages link - hidden on mobile as it's available as permanent FAB button */}
              {!isPageDisabled('messages') && canAccess.messages && !isMobile && (
                <NavLink
                  to="/messages"
                  icon={<MessageCircle className="h-4 w-4" />}
                  text="Messages"
                  badgeCount={unreadMessagesCount}
                />
              )}
              
              {/* Calls link - REMOVED: Now accessible via dial pad icon in top navigation */}
              
              {/* Divider 1 */}
              <div className="my-3 border-t border-gray-700"></div>
              
              {/* Section 2: Operations & Management */}
              {canAccess.calendar && shouldShowPage('calendarV2', hasCalendar) && (
                <NavLink
                  to="/calendarV2"
                  icon={<Calendar className="h-4 w-4" />}
                  text="Location Calendar"
                />
              )}
              
              {canAccess.calendar && shouldShowPage('mobile-calendar', hasCalendar) && (
                <NavLink
                  to="/mobile-calendar"
                  icon={<Calendar className="h-4 w-4" />}
                  text="Mobile Calendar"
                />
              )}
              
              {proposalsAccess !== 'disabled' && canAccess.proposals && (
                <NavLink
                  to="/proposals"
                  icon={<FileText className="h-4 w-4" />}
                  text="Proposals"
                />
              )}
              
              {shouldShowPage('bookings', hasBookings) && canAccess.bookings && (
                <NavLink
                  to="/bookings"
                  icon={<FileBarChart2 className="h-4 w-4" />}
                  text="Bookings"
                />
              )}
              
              {!isPageDisabled('resources') && canAccess.resources && (
                <NavLink
                  to="/resources"
                  icon={<UserCircle className="h-4 w-4" />}
                  text="Team"
                  badgeCount={pendingTimeOffCount}
                />
              )}
              
              {!isPageDisabled('services') && canAccess.services && (
                <NavLink
                  to="/services"
                  icon={<Wrench className="h-4 w-4" />}
                  text="Services"
                />
              )}
              
              {shouldShowPage('customers', hasCustomers) && canAccess.customers && (
                <NavLink
                  to="/customers"
                  icon={<Users className="h-4 w-4" />}
                  text="Customers"
                />
              )}
              
              <ProductsDropdown canAccess={canAccess} isPageDisabled={isPageDisabled} />
              
              {/* Divider 2 */}
              <div className="my-3 border-t border-gray-700"></div>
              
              {/* Section 3: Business Management */}
              {!isPageDisabled('crm') && canAccess.crm && (
                <NavLink
                  to="/crm"
                  icon={<Users className="h-4 w-4" />}
                  text="CRM"
                  badgeCount={unreadOpportunitiesCount}
                />
              )}
              
              {!isPageDisabled('marketing') && canAccess.marketing && (
                <NavLink
                  to="/marketing"
                  icon={<Megaphone className="h-4 w-4" />}
                  text="Marketing"
                />
              )}
              
              {canAccess.timeAccounting && (
                <NavLink
                  to="/time-accounting"
                  icon={<Calculator className="h-4 w-4" />}
                  text="Time / Accounting"
                  badgeCount={pendingPayrollRequestsCount}
                />
              )}
              
              {!isPageDisabled('automations') && canAccess.automations && (
                <NavLink
                  to="/automations"
                  icon={<Activity className="h-4 w-4" />}
                  text="Automations"
                />
              )}
              
              {shouldShowPage('tasks', hasTasks) && canAccess.tasks && (
                <NavLink
                  to="/tasks"
                  icon={<ListChecks className="h-4 w-4" />}
                  text="Tasks"
                />
              )}
              
              {/* Divider 3 */}
              <div className="my-3 border-t border-gray-700"></div>
              
              {/* Section 4: Reports Dropdown */}
              <ReportsDropdown 
                canAccess={canAccess}
                isPageDisabled={isPageDisabled}
              />
              
              {/* Divider 4 */}
              <div className="my-3 border-t border-gray-700"></div>
              
              {/* Section 5: Settings */}
              {shouldShowPage('settings', hasSettings) && canAccess.settings && (
                <NavLink
                  to="/settings"
                  icon={<Settings2 className="h-4 w-4" />}
                  text="Settings"
                />
              )}

              {/* App Store - Always visible */}
              <NavLink
                to="/app-store"
                icon={<Store className="h-4 w-4" />}
                text="App Store"
                badge="New"
              />
                
              {/* Super Admin link */}
              {isSuperAdmin && (
                <NavLink
                  to="/super-admin"
                  icon={<Shield className="h-4 w-4" />}
                  text="Super Admin"
                />
              )}

            </div>
            
            {/* Add extra padding space at bottom when on mobile to ensure everything is accessible */}
            {isMobile && !isPWA && <div className="h-16"></div>}
          </div>
        </div>
      )}
    </nav>
  );
}
